<script setup lang="ts">
import type { CharacterPageData } from '~/composables/useCharacterPage'

defineProps<{
  character: CharacterPageData
}>()

const emit = defineEmits<{
  'appeal-submitted': []
}>()

const authStore = useAuthStore()
const toast = useToast()
const submitting = ref(false)

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  })
}

async function handleAppeal() {
  submitting.value = true
  const success = await authStore.submitAppeal()
  submitting.value = false

  if (success) {
    toast.add({
      title: 'Appeal submitted',
      description: 'Your appeal has been submitted for review.',
      icon: 'i-lucide-check',
      color: 'success',
      duration: 3000
    })
    emit('appeal-submitted')
  } else {
    toast.add({
      title: 'Error',
      description: authStore.error || 'Failed to submit appeal',
      icon: 'i-lucide-x',
      color: 'error'
    })
  }
}
</script>

<template>
  <div class="min-h-screen flex items-center justify-center p-6">
    <div class="w-full max-w-md space-y-6">
      <!-- Header -->
      <div class="text-center space-y-3">
        <div class="mx-auto flex items-center justify-center size-16 rounded-full bg-red-500/10">
          <UIcon
            name="i-lucide-shield-alert"
            class="size-8 text-red-400"
          />
        </div>
        <h1 class="text-2xl font-bold text-white">
          Page Blocked
        </h1>
        <p class="text-sm text-white/50">
          Your character page has been blocked by an administrator.
        </p>
      </div>

      <!-- Block reason card -->
      <div
        v-if="character.block"
        class="rounded-xl border border-red-500/20 bg-red-500/5 p-4 space-y-3"
      >
        <div class="flex items-center gap-2">
          <UIcon
            name="i-lucide-message-circle"
            class="size-4 text-red-400 shrink-0"
          />
          <span class="text-xs font-medium text-red-300 uppercase tracking-wider">Reason</span>
        </div>
        <p class="text-sm text-white/80">
          {{ character.block.reason }}
        </p>
        <p class="text-xs text-white/40">
          Blocked on {{ formatDate(character.block.blocked_at) }}
        </p>
      </div>

      <!-- Appeal section -->
      <div
        v-if="character.block && !character.block.appeal_submitted_at"
        class="space-y-3"
      >
        <p class="text-sm text-white/50 text-center">
          If you believe this was a mistake or have made changes, you can submit an appeal.
        </p>
        <div class="flex justify-center">
          <UButton
            label="Submit Appeal"
            icon="i-lucide-send"
            color="primary"
            :loading="submitting"
            @click="handleAppeal"
          />
        </div>
      </div>

      <!-- Already appealed -->
      <div
        v-else-if="character.block?.appeal_submitted_at"
        class="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 space-y-2"
      >
        <div class="flex items-center gap-2">
          <UIcon
            name="i-lucide-clock"
            class="size-4 text-amber-400 shrink-0"
          />
          <span class="text-sm font-medium text-amber-300">Appeal Pending</span>
        </div>
        <p class="text-xs text-white/50">
          Your appeal was submitted on {{ formatDate(character.block.appeal_submitted_at) }}.
          An administrator will review it soon.
        </p>
      </div>

      <!-- Customize link -->
      <div class="text-center">
        <NuxtLink
          to="/customize"
          class="text-sm text-primary-400 hover:text-primary-300 transition-colors"
        >
          Go to customization settings
        </NuxtLink>
      </div>
    </div>
  </div>
</template>
